
import Locale from 'element-ui/lib/mixins/locale'
import size from './mixins/size'

import { isFunction } from 'vessel/utils'

import TableRow from './row'
import TableHead from './head'

const isGroup  = ({ tag }) => tag && tag.endsWith('VsTableGroup')
const isColumn = ({ tag }) => tag && tag.endsWith('VsTableColumn')

export default {
  name: 'VsTable',
  inheritAttrs: false,
  mixins: [Locale, size],
  components: {
    TableHead,
    TableRow
  },
  provide() {
    return {
      table: this,
      $t: this.t
    }
  },
  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    border: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: null
    },
    stripe: {
      type: Boolean,
      default: false
    },
    wrapColumn: {
      type: Boolean,
      default: null
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    darkHeader: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: null
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    defaultSort: {
      type: Object,
      default: () => ({})
    }
  },
  vessel: {
    wrapper: {
      $attrs: 'all',
      overflow: 'auto',
      color: 'grey-darkest',
      $hasOutline: {
        borderWidth: val => val ? 1 : null
      }
    },
    table: {
      minWidth: 'full',
      backgroundColor: 'white',
      fontSize: 'sm',
      borderCollapse: 'separate'
    },
    head: {
      color: 'grey-dark',
      userSelect: 'none'
    },
    empty: {
      color: 'grey-dark',
      padding: 'xs',
      userSelect: 'none',
      whiteSpace: 'normal',
      textAlign: 'center'
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(value) {
        if (value && value.length) {
          this.selectedSize && this.toggleAllSelection(false)
          this.expandedSize && this.toggleAllExpansion(false)
        }
      }
    },
    selectedSize(value, oldValue) {
      if (value !== oldValue) {
        this.$emit('selection-change', [...this.selected])
      }
    },
    expandedSize(value, oldValue) {
      if (value !== oldValue) {
        this.$emit('expand-change', [...this.expanded])
      }
    }
  },
  data() {
    return {
      sorting: Object(this.defaultSort),
      selected: new Set(),
      expanded: new Set(),
      selectedSize: 0,
      expandedSize: 0,
      colgroups: [],
      colslot: 'default',
      colspan: 1
    }
  },
  computed: {
    hasBorder() {
      return this.border
    },
    hasOutline() {
      return this.outline == null ? this.border : this.outline
    },
    dataSize() {
      return this.data ? this.data.length : 0
    },
    hasData() {
      return this.dataSize > 0
    }
  },
  methods: {
    toggleRowState(key, row, enable) {
      const set = this[key]
      enable ? set.add(row) : set.delete(row)

      this[`${key}Size`] = set.size
    },
    toggleAllState(key, enable) {
      const set = this[key]
      enable ? this.data.forEach(item => set.add(item)) : set.clear()

      this[`${key}Size`] = set.size
    },
    toggleRowSelection(row, selected) {
      this.toggleRowState('selected', row, selected)
    },
    toggleAllSelection(selected) {
      this.toggleAllState('selected', selected)
    },
    toggleRowExpansion(row, expanded) {
      this.toggleRowState('expanded', row, expanded)
    },
    toggleAllExpansion(expanded) {
      this.toggleAllState('expanded', expanded)
    },
    getRowKey(row, index) {
      if (isFunction(this.rowKey)) {
        return this.rowKey.call(null, row, index)
      } else {
        return row[this.rowKey] || index
      }
    },
    renderColumns() {
      if (this.$slots.__column__ || !this.$slots.default) return

      let headersIndex = 1

      const columnSlot = []
      const groupItems = []
      const columnData = []

      const hasGrouped = this.$slots.default.some(isGroup)

      const headerSlot = (child, propsData) => {
        const { children } = child.componentOptions || {}
        const { header } = child.data.scopedSlots || {}

        const node = children &&
          children.find(({ data }) => data && data.slot == 'header')

        if (header || node) {
          const slot = `__header${headersIndex}__`

          propsData.slotKey = slot
          this.$slots[slot] = node || header({ label: propsData.label })

          headersIndex++
        }
      }

      const parseChild = (child, asGroup) => {
        const { propsData } = child.componentOptions

        headerSlot(child, propsData)

        if (asGroup) {
          groupItems.push({ rowspan: 2, ...propsData })
        } else {
          columnData.push(propsData)
        }

        if (hasGrouped) {
          columnSlot.push(child)
        }
      }

      const parseGroup = group => {
        const { children, propsData } = group.componentOptions

        headerSlot(group, propsData)

        const items = children.filter(isColumn)
        groupItems.push({ colspan: items.length, ...propsData })

        items.forEach(child => parseChild(child))
      }

      this.$slots.default.forEach(child => {
        if (isColumn(child)) {
          parseChild(child, hasGrouped)
        } else if (isGroup(child)) {
          parseGroup(child)
        }
      })

      if (hasGrouped) {
        this.colslot = '__column__'
        this.colspan = columnSlot.length
      } else {
        this.colslot = 'default'
        this.colspan = this.$slots.default.filter(isColumn).length
      }

      this.$slots.__column__ = columnSlot
      this.colgroups = [groupItems, columnData]
    }
  },
  created() {
    this.renderColumns()
  },
  beforeUpdate() {
    this.renderColumns()
  }
}
